import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { fileUrl } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";
import { UploadButton } from "../UploadButton";
import { StatusPill } from "../StatusPill";
import { EmptyState } from "../Loader";
import { fmtDate } from "../../lib/format";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, Boxes } from "lucide-react";

function FieldInput({ field, value, onChange }) {
  const v = value ?? (field.type === "checkbox" ? false : field.type === "image" ? [] : "");
  if (field.type === "textarea") return <Textarea value={v} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={`field-${field.key}`} />;
  if (field.type === "number") return <Input type="number" value={v} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={`field-${field.key}`} />;
  if (field.type === "date") return <Input type="date" value={v || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.key}`} />;
  if (field.type === "checkbox") return <div className="flex items-center h-9"><Checkbox checked={!!v} onCheckedChange={(x) => onChange(!!x)} data-testid={`field-${field.key}`} /></div>;
  if (field.type === "select") return (
    <Select value={v} onValueChange={onChange}>
      <SelectTrigger data-testid={`field-${field.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
      <SelectContent>{field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
  if (field.type === "image") {
    const arr = Array.isArray(v) ? v : [];
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <UploadButton accept="image/*,video/*" multiple label="Add" variant="outline" onUploaded={(r) => onChange([...arr, r.url])} testid={`field-${field.key}-upload`} />
        {arr.map((u, i) => (
          <div key={i} className="relative h-14 w-14 rounded-md overflow-hidden border border-border bg-arq-surface">
            <img src={u.startsWith("http") ? u : fileUrl(u)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(arr.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/60 rounded-full p-0.5"><X className="h-3 w-3 text-white" /></button>
          </div>
        ))}
      </div>
    );
  }
  return <Input value={v} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.key}`} />;
}

export function CrudManager({ title, listUrl, createUrl, itemBase, queryKey, fields, columns, statusKey, defaults = {}, uploadDoc = false }) {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey, queryFn: async () => (await api.get(listUrl)).data });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const openNew = () => { setEditing(null); setForm({ ...defaults }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) await api.patch(`${itemBase}/${editing.id}`, form);
      else await api.post(createUrl, form);
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (it) => {
    await api.delete(`${itemBase}/${it.id}`);
    qc.invalidateQueries({ queryKey });
    toast.success("Deleted");
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cell = (it, col) => {
    const val = it[col.key];
    if (col.status) return <StatusPill status={val} />;
    if (col.date) return fmtDate(val);
    if (col.key.includes("photos")) return (val && val.length) || 0;
    return val ?? "—";
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{items.length} {title.toLowerCase()}</p>
        <Button size="sm" onClick={openNew} data-testid={`add-${title.toLowerCase().replace(/\s+/g, "-")}`}><Plus className="h-4 w-4" />Add {title}</Button>
      </div>
      {items.length === 0 ? <EmptyState icon={Boxes} title={`No ${title.toLowerCase()} yet`} /> : (
        <div className="bg-arq-card border border-border rounded-md overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-arq-surface/60 text-left">
              <tr>{columns.map((c) => <th key={c.key} className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold whitespace-nowrap">{c.label}</th>)}<th /></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-arq-surface/30" data-testid={`row-${it.id}`}>
                  {columns.map((c) => <td key={c.key} className="px-4 py-3 align-top">{cell(it, c)}</td>)}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(it)} className="p-1.5 hover:bg-arq-surface rounded-md" data-testid={`edit-${it.id}`}><Pencil className="h-4 w-4 text-muted-foreground" /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button className="p-1.5 hover:bg-arq-surface rounded-md" data-testid={`delete-${it.id}`}><Trash2 className="h-4 w-4 text-destructive" /></button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete this {title.toLowerCase()}?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(it)} data-testid={`confirm-delete-${it.id}`}>Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="crud-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} {title}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <Label className="mb-1.5 block">{f.label}</Label>
                <FieldInput field={f} value={form[f.key]} onChange={(v) => set(f.key, v)} />
              </div>
            ))}
            {uploadDoc && (
              <div className="sm:col-span-2">
                <Label className="mb-1.5 block">Document File</Label>
                <UploadButton accept="*/*" variant="outline" label="Upload document" onUploaded={(r) => { set("file_id", r.id); set("url", r.url); toast.success("File attached"); }} testid="doc-upload" />
                {form.file_id && <p className="text-xs text-muted-foreground mt-1">File attached ✓</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="crud-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
